import { makePathFinderLineComponent, pathFinderLineComponentType } from '@/_models/virtualStaging/PathFinderLine/component/PathFinderLine.component';
import { ITargetObjectData } from '@/_models/virtualStaging/PathFinderLine/types';
import { getHorizontalRotationBetweenPoints } from '@/utils/three/getHorizontalRotationBetweenPoints';
import { timeout } from '@/utils/timeout';

export class ScenePathFinderLine {
  private showCaseTemp;

  private sweepGraph;

  private camera;

  private currentSweep;

  public setActionPathNavbarVisible;

  private pathArrowRef;

  public moveToSweep;

  private component;

  private currentSID;

  private targetSID;

  public targetObjectData: any;

  public destinationTitle = 'Move to location';

  public inAutoNavigate: boolean;

  public onLoaded: any;

  constructor(showCaseTemp, camera, currentSweep, setActionPathNavbarVisible, pathArrowRef, moveToSweep) {
    this.showCaseTemp = showCaseTemp;
    this.camera = camera;
    this.currentSweep = currentSweep;
    this.setActionPathNavbarVisible = setActionPathNavbarVisible;
    this.pathArrowRef = pathArrowRef;
    this.moveToSweep = moveToSweep;
    this.create();
  }

  async create() {
    this.sweepGraph = await this.showCaseTemp.mpSDK.Sweep.createGraph();
    for (const { src, dst, weight } of this.sweepGraph.edges) {
      this.sweepGraph.setEdge({ src, dst, weight: weight ** 1.5 });
    }

    await this.showCaseTemp.showcase.Scene.register(pathFinderLineComponentType, makePathFinderLineComponent);
    const modelNode: any = await this.showCaseTemp.mpSDK.Scene.createNode();
    this.component = await modelNode.addComponent(pathFinderLineComponentType, {
      camera: this.camera,
      arrowRef: this.pathArrowRef,
    });
    await modelNode.start();
    if (this.onLoaded) this.onLoaded();
    this.initSweepUpdates();
  }

  initSweepUpdates() {
    this.showCaseTemp.showcase.Sweep.current.subscribe(async (currentSweep) => {
      if (currentSweep.sid || currentSweep.sweep) {
        this.currentSID = currentSweep.sid || currentSweep.sweep;
        if (!this.targetSID) return;
        if (this.currentSID === this.targetSID) {
          this.reset();
          return;
        }
        this.update(this.currentSID, this.targetSID);
      }
    });
  }

  autoNavigateCurrentPath() {
    this.inAutoNavigate = true;
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      const { path } = this.component.inputs;
      try {
        for (let i = 0; i < path.length - 2; i += 2) {
          const currentPoint = path[i];
          const nextPoint = path[i + 2] === path[path.length - 1] ? path[path.length - 2] : path[i + 2];
          if (!this.inAutoNavigate || this.currentSweep?.uuid === '') {
            resolve(false);
            break;
          }
          if (nextPoint) {
            const angleDegrees = getHorizontalRotationBetweenPoints(currentPoint, nextPoint);
            // eslint-disable-next-line no-await-in-loop
            await this.moveToSweep(nextPoint.data.sid, { x: 0, y: angleDegrees }, 'FLY');

            // sleep function to prevent tour crash when trying to move during automatic movement
            // eslint-disable-next-line no-await-in-loop
            await timeout(100);
          }
        }
        resolve(true);
      } catch (error) {
        console.error('Error in autoNavigate:', error);
        reject(error);
      }
    }).then((completed) => {
      if (this.inAutoNavigate && completed) {
        this.showCaseTemp.showcase.Mattertag.navigateToTag(this.targetObjectData.sid, this.showCaseTemp.showcase.Mattertag.Transition.FLY);
        this.reset();
      }
    });
  }

  async update(currentSID, targetSID, title?: string, targetObjectData?: ITargetObjectData) {
    if (title) this.destinationTitle = title;
    if (targetObjectData) this.targetObjectData = targetObjectData;

    this.currentSID = currentSID;
    this.targetSID = targetSID;

    const startSweep = this.sweepGraph.vertex(currentSID);
    const endSweep = this.sweepGraph.vertex(targetSID);

    const AStarRunner = this.showCaseTemp.mpSDK.Graph.createAStarRunner(this.sweepGraph, startSweep, endSweep);
    const { path } = AStarRunner.exec();
    this.component.inputs.path = path;
    const visible = this.targetObjectData.enableNavBar || this.targetObjectData.enableTeleportBtn || this.targetObjectData.autoNavigation;
    this.setActionPathNavbarVisible(visible);
    AStarRunner.dispose();
  }

  async reset() {
    this.currentSID = null;
    this.targetSID = null;
    this.component.inputs.path = [];
    this.destinationTitle = 'Move to location';
    this.setActionPathNavbarVisible(false);
    this.targetObjectData = null;
    this.inAutoNavigate = false;
  }
}
